// SourceRenderer — the per-reading-shape renderer registry (the O(n) half of
// the seam; source-view-per-kind.md §2.2). The O(1) chrome lives in
// `SourceView` (the shell); this file owns everything kind-specific.
//
// The shell renders `<SourceReading projection … />` in its protagonist slot.
// `SourceReading` picks `RENDERERS[shapeOf(projection)]` — one lookup keyed by
// the CLOSED reading shape, never the open kind sum. Each renderer NARROWS the
// projection to its expected variant with a type guard (no `as`), and any
// projection it doesn't recognise degrades to `Flat` (§4 rule 1) — the
// universal floor, since every readable projection yields at least paragraphs.
//
// A11y: highlight marking never rides on tint alone (a hidden "Highlighted:"
// prefix speaks it); the per-block note affordance reveals on hover/focus but
// stays in the a11y tree (opacity, never display:none); the PDF "Sida x av y"
// page control (typed input + prev/next) is the keyboard-navigable counterpart
// to the aria-hidden canvases (whose readable equivalent is the Extracted-text toggle).

import { exhaustSwitchCase } from "@bilda/utils";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { Button } from "../Button/Button.js";
import { EmptyState } from "../EmptyState/EmptyState.js";
import { HelpHint } from "../HelpHint/HelpHint.js";
import { Icon } from "../Icon/Icon.js";
import type { PageSpan } from "../Navigation/types.js";
import { SegmentedControl } from "../SegmentedControl/SegmentedControl.js";
import { centerInScrollParent, scrollParentOf } from "../scrollParent.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import { AnnotatedBlock } from "./AnnotatedBlock.js";
import { PageControl } from "./PageControl.js";
import { type PdfMarker, PdfPages } from "./PdfPages.js";
import { clampPage } from "./pageNav.js";
import { blocksOf, type ReadingShape, shapeOf } from "./readingShape.js";
import { SyncedTranscriptRenderer } from "./SyncedTranscriptRenderer.js";
import type {
    HighlightedBlock,
    PdfContent,
    SourceAnnotation,
    SourceBlock,
    SourceProjection,
    UnavailableReason,
} from "./types.js";

// === The renderer contract ===================================================

// What every renderer needs beyond the projection: the two report-up intents
// the reading blocks carry, PLUS the live select→annotate surface's own two
// inputs (`sourceId`/`annotations` — see `AnnotatedBlock.tsx`'s header for
// why it reaches `useAppPorts()` itself rather than a third report-up
// callback here). All four optional — a read-only host, or one that never
// supplied `meta.sourceId`, simply gets no new gesture affordances.
export type ReadingDeps = {
    // The author clicked a highlighted passage — the host inspects it.
    readonly onInspectHighlight?: (blockId: string) => void;
    // The author opened the note composer for a block (`openNoteComposer`).
    readonly onAnnotate?: (blockId: string) => void;
    // This source's own resource id — threads into `AnnotatedBlock` so it
    // can dispatch `placeHighlight`/`placeAnnotation`/`introduceToContext`.
    readonly sourceId?: string;
    // The source's placed annotations (comment + highlight), grouped by
    // block inside `BlockView` below.
    readonly annotations?: readonly SourceAnnotation[];
    // A page span to open at and mark (a chat tool's read locator). Only the
    // paginated (PDF) reader can honour it; the others open at the top.
    readonly focusPages?: PageSpan;
    // Re-upload the original file INTO this source — offered only by the
    // `content-missing` floor. Absent ⇒ the floor explains without a button.
    readonly onReupload?: () => void;
    // Referenced passages (numbered, with their page) to mark on the PDF
    // pages; the selected one and the click on a marker (#89).
    readonly markers?: readonly PdfMarker[];
    readonly selectedMarker?: number;
    readonly onMarker?: (n: number) => void;
};

type ReadingProps = { readonly projection: SourceProjection } & ReadingDeps;
type ReadingRenderer = (props: ReadingProps) => ReactNode;

// === One reading block =======================================================

// A total match, so a future block variant must choose a rendering before it
// compiles. The optional annotate affordance reveals on row hover/focus. A
// "plain" block promotes to the LIVE select→annotate surface
// (`AnnotatedBlock`) — inline tint + gutter badges for its own placed
// marks, the floating toolbar on selection. "highlighted" blocks (the
// legacy whole-block anchor) are UNCHANGED — a separate, older mechanism
// this build does not touch. A highlighted block that carries reference
// numbers shows them in the margin (`MarkedBlock`).
const BlockView = ({
    block,
    onInspectHighlight,
    onAnnotate,
    sourceId,
    annotations = [],
    selectedMarker,
    onMarker,
}: {
    readonly block: SourceBlock;
} & ReadingDeps): ReactNode => {
    const { t } = useLingui();
    const body = (() => {
        switch (block.type) {
            case "plain":
                return (
                    // `data-source-block-id` is SourceView.tsx's find-bar
                    // hook: its DOM-highlight effect locates this block by
                    // this attribute, then walks ITS text nodes only — a
                    // scoped read, not a new interactive affordance.
                    <p
                        className="bilda-source-view__block"
                        data-source-block-id={block.id}
                    >
                        <AnnotatedBlock
                            blockId={block.id}
                            text={block.text}
                            sourceId={sourceId}
                            annotations={annotations.filter(
                                (a) => a.blockId === block.id,
                            )}
                        />
                    </p>
                );
            case "highlighted":
                return (
                    <MarkedBlock
                        block={block}
                        selectedMarker={selectedMarker}
                        onMarker={onMarker}
                    >
                        <p className="bilda-source-view__block">
                            <button
                                type="button"
                                className="bilda-source-view__highlight"
                                data-selected={
                                    selectedMarker !== undefined &&
                                    block.markers?.includes(selectedMarker)
                                        ? "true"
                                        : undefined
                                }
                                onClick={() => {
                                    const first = block.markers?.[0];
                                    if (first !== undefined) onMarker?.(first);
                                    onInspectHighlight?.(block.id);
                                }}
                            >
                                <VisuallyHidden>
                                    <Trans>Highlighted:</Trans>
                                </VisuallyHidden>
                                {block.text}
                            </button>
                        </p>
                    </MarkedBlock>
                );
            default:
                return exhaustSwitchCase(block);
        }
    })();
    if (onAnnotate === undefined) return body;
    return (
        <div className="bilda-source-view__block-row">
            {body}
            <Button
                variant="ghost"
                size="sm"
                className="bilda-source-view__annotate"
                onClick={() => onAnnotate(block.id)}
            >
                {t`Add note`}
            </Button>
        </div>
    );
};

// The reading canvas — the sectioned paragraphs, shared by Flat and reused by
// the Paginated "Extracted text" toggle.
// `verbatim`: the text IS the original (a text file), so its own line breaks
// stay (a list keeps its lines); extracted text reflows.
const BlocksReading = ({
    blocks,
    verbatim = false,
    onInspectHighlight,
    onAnnotate,
    sourceId,
    annotations,
    selectedMarker,
    onMarker,
}: {
    readonly blocks: readonly SourceBlock[];
    readonly verbatim?: boolean;
} & ReadingDeps): ReactNode => (
    <article
        className={`bilda-source-view__reading${
            verbatim ? " bilda-source-view__reading--verbatim" : ""
        }`}
    >
        {blocks.map((block) => (
            <BlockView
                key={block.id}
                block={block}
                onInspectHighlight={onInspectHighlight}
                onAnnotate={onAnnotate}
                sourceId={sourceId}
                annotations={annotations}
                selectedMarker={selectedMarker}
                onMarker={onMarker}
            />
        ))}
    </article>
);

// A marked passage with its reference numbers in the margin (#89, E-06): the
// same numbers as the cards beside the source. Choosing a card brings its
// passage into view: a render-edge DOM scroll keyed on the projected
// selection, moving only the nearest scrolling box.
const MarkedBlock = ({
    block,
    selectedMarker,
    onMarker,
    children,
}: {
    readonly block: HighlightedBlock;
    readonly selectedMarker: number | undefined;
    readonly onMarker: ((n: number) => void) | undefined;
    readonly children: ReactNode;
}): ReactNode => {
    const { t } = useLingui();
    const ref = useRef<HTMLDivElement>(null);
    const markers = block.markers ?? [];
    const holdsSelected =
        selectedMarker !== undefined && markers.includes(selectedMarker);
    useEffect(() => {
        if (holdsSelected && ref.current !== null)
            centerInScrollParent(ref.current);
    }, [holdsSelected]);
    if (markers.length === 0) return children;
    return (
        <div className="bilda-source-view__marked" ref={ref}>
            <span className="bilda-source-view__marked-numbers">
                {markers.map((n) => (
                    <button
                        key={n}
                        type="button"
                        className="bilda-pdf-marker"
                        data-selected={
                            n === selectedMarker ? "true" : undefined
                        }
                        aria-pressed={n === selectedMarker}
                        aria-label={t`Reference ${n}`}
                        onClick={() => onMarker?.(n)}
                    >
                        {n}
                    </button>
                ))}
            </span>
            {children}
        </div>
    );
};

// === Flat — plain text (ReadingShape.flat) ===================================

// Today's behaviour, promoted to first-class AND the universal fallback (§4
// rule 1). Empty blocks (only reachable via a degraded fallback) show the
// honest empty state rather than a blank canvas.
const FlatReading: ReadingRenderer = ({ projection, ...deps }) => {
    const { t } = useLingui();
    const blocks = blocksOf(projection);
    if (blocks.length === 0) {
        return (
            <EmptyState
                title={t`Nothing to read here yet`}
                body={t`No readable text has been extracted from this source.`}
            />
        );
    }
    return (
        <BlocksReading
            blocks={blocks}
            verbatim={projection.type === "text"}
            {...deps}
        />
    );
};

// === Paginated — PDF (ReadingShape.paginated) ================================

// Scroll a page into view from the page control. An interaction-edge DOM read (the
// canvases are owned by PdfPages' effect) — not a render-time effect.
// Only the nearest scrolling ancestor moves: `scrollIntoView` would also
// scroll every outer box, shifting the whole app frame when the reader sits in
// the right panel.
// The sticky page control stays over the page's top, so the page lands just
// under it (its label and markers stay in view).
const scrollToPage = (idPrefix: string, page: number): void => {
    const slot = document.getElementById(`${idPrefix}-page-${page}`);
    const scroller = slot === null ? null : scrollParentOf(slot);
    if (slot === null || scroller === null) return;
    const control = slot
        .closest(".bilda-source-view__paginated-main")
        ?.querySelector(".bilda-source-view__page-control");
    scroller.scrollBy({
        top:
            slot.getBoundingClientRect().top -
            scroller.getBoundingClientRect().top -
            (control?.getBoundingClientRect().height ?? 0),
        behavior: "smooth",
    });
};

// The original pages (via PdfPages) plus the "Sida x av y" page control and
// the Pages | Extracted text toggle. Page IMAGES are ready today; page-anchored
// TEXT waits on ingest emitting page boundaries (§3.1) — the toggle bridges the
// gap (pages for fidelity, extracted text for AT and copy/quote).
const PaginatedReading = ({
    projection,
    focusPages,
    markers,
    selectedMarker,
    onMarker,
    ...deps
}: ReadingProps): ReactNode => {
    const { t } = useLingui();
    const rawId = useId();
    const idPrefix = `bilda-pdf${rawId}`;
    const [presentation, setPresentation] = useState<"pages" | "text">("pages");
    const [pageCount, setPageCount] = useState<number | null>(null);
    // View-local: the page most in view (scroll-tracked), not domain state.
    const [currentPage, setCurrentPage] = useState(1);
    // Open at the span's first page once the page slots exist: a render-edge
    // DOM scroll keyed on the projected `focusPages`; the only state it
    // writes is the view-local current page.
    const focusFirst = focusPages?.first;
    useEffect(() => {
        if (focusFirst === undefined || pageCount === null || pageCount < 1)
            return;
        const page = clampPage(focusFirst, pageCount);
        setCurrentPage(page);
        scrollToPage(idPrefix, page);
    }, [focusFirst, pageCount, idPrefix]);

    // §4.1 fallback — unreachable (shapeOf guarantees paginated ⇔ pdf), but the
    // guard keeps the narrowing honest and cast-free.
    if (projection.type !== "pdf") {
        return <FlatReading projection={projection} {...deps} />;
    }
    const pdf: PdfContent = projection;

    // No extracted text → nothing to toggle to; pages only.
    const hasText = pdf.blocks.length > 0;
    const showPages = !hasText || presentation === "pages";

    return (
        <div className="bilda-source-view__pdf-section">
            {hasText ? (
                <div className="bilda-source-view__pdf-toggle">
                    <SegmentedControl
                        ariaLabel={t`Source presentation`}
                        size="sm"
                        value={presentation}
                        onChange={(value) =>
                            setPresentation(value === "text" ? "text" : "pages")
                        }
                        options={[
                            { value: "pages", label: t`Pages` },
                            { value: "text", label: t`Extracted text` },
                        ]}
                    />
                    {/* H14 (inline-help.md): what "Extracted text" IS, since the
                        segment's own label is plain text (SegmentedOption.label
                        is a caller string, not a node — the hint sits beside the
                        control instead of inside a segment). */}
                    <HelpHint label={t`Explanation: extracted text`}>
                        <Trans>
                            The text read out of the file. This is what the
                            assistant and the quality checks work with.
                        </Trans>
                    </HelpHint>
                </div>
            ) : null}
            {showPages ? (
                <div className="bilda-source-view__paginated">
                    <div className="bilda-source-view__paginated-main">
                        {pageCount !== null && pageCount > 0 ? (
                            <PageControl
                                current={currentPage}
                                pageCount={pageCount}
                                onJump={(page) => {
                                    setCurrentPage(page);
                                    scrollToPage(idPrefix, page);
                                }}
                            />
                        ) : null}
                        <PdfPages
                            data={pdf.data}
                            idPrefix={idPrefix}
                            markedPages={focusPages}
                            onPageCount={setPageCount}
                            onCurrentPage={setCurrentPage}
                            markers={markers}
                            selectedMarker={selectedMarker}
                            onMarker={onMarker}
                        />
                    </div>
                </div>
            ) : (
                <BlocksReading blocks={pdf.blocks} {...deps} />
            )}
        </div>
    );
};

// === Synced — video/audio (ReadingShape.synced) ==============================

// A continuous clock (playback) drives a cursor over time-addressed
// transcript lines — video-source-view-synced-transcript.md §0/§2. The sync
// logic itself is `SyncedTranscriptRenderer`'s pure decider
// (`syncedTranscript.ts`); this wrapper only narrows the projection. Owner
// decision 4 (§7 of the doc) scopes MVP to read+sync — no annotate-on-video
// yet, so `onInspectHighlight`/`onAnnotate` have nothing to attach to and are
// dropped here, same as `UnavailableReading` below.
const SyncedReading = ({ projection, ...deps }: ReadingProps): ReactNode => {
    // §4.1 fallback — unreachable (shapeOf guarantees synced ⇔ video), but the
    // guard keeps the narrowing honest and cast-free.
    if (projection.type !== "video") {
        return <FlatReading projection={projection} {...deps} />;
    }
    return <SyncedTranscriptRenderer content={projection} />;
};

// === Native file kinds (ReadingShape.image / .media) =========================

// A GENERIC SUBDOMAIN (source-view-per-kind.md §2): rendering is not our core,
// so image/plain-audio/plain-video are shown CONVENTIONALLY via the browser's
// own standard elements. These two renderers ARE the anti-corruption layer —
// the standard-tool boundary — so no DOM/vendor specifics leak into the domain
// `SourceContent` (it carries only a `src` URL + our own `kind` discriminant).
// Stage 2's office libs (mammoth/SheetJS) plug into this same registry behind a
// heavier ACL. These kinds bear no reading blocks; painting anchors onto the
// file is deferred (§7) — this just shows the file. Like `SyncedReading`/
// `UnavailableReading`, they drop the block-anchored gesture deps (nothing to
// attach to).

// A single still, shown natively. `alt` is the source filename (the projection
// carries it as `label`) — the accessible name for the image.
const ImageReading = ({ projection, ...deps }: ReadingProps): ReactNode => {
    // §4.1 fallback — unreachable (shapeOf guarantees image ⇔ image), but the
    // guard keeps the narrowing honest and cast-free.
    if (projection.type !== "image") {
        return <FlatReading projection={projection} {...deps} />;
    }
    return (
        <figure className="bilda-source-view__native">
            <img
                className="bilda-source-view__native-image"
                src={projection.src}
                alt={projection.label}
            />
        </figure>
    );
};

// Plain audio/video WITHOUT a transcript, played natively with the browser's
// own `controls`. The distinct-per-`kind` elements keep the two native
// controls exact (never one element with a swapped attribute). Children are
// the localized "can't play" floor older browsers render in place of controls;
// `aria-label` names the file for AT.
const MediaReading = ({ projection, ...deps }: ReadingProps): ReactNode => {
    const { t } = useLingui();
    // §4.1 fallback — unreachable (shapeOf guarantees media ⇔ media).
    if (projection.type !== "media") {
        return <FlatReading projection={projection} {...deps} />;
    }
    const cannotPlay = t`Your browser can't play this file.`;
    switch (projection.kind) {
        case "audio":
            return (
                <figure className="bilda-source-view__native">
                    {/* biome-ignore lint/a11y/useMediaCaption: plain media has no transcript (§7). */}
                    <audio
                        className="bilda-source-view__native-audio"
                        controls
                        src={projection.src}
                        aria-label={projection.label}
                    >
                        {cannotPlay}
                    </audio>
                </figure>
            );
        case "video":
            return (
                <figure className="bilda-source-view__native">
                    {/* biome-ignore lint/a11y/useMediaCaption: plain media has no transcript (§7). */}
                    <video
                        className="bilda-source-view__native-video"
                        controls
                        src={projection.src}
                        aria-label={projection.label}
                    >
                        {cannotPlay}
                    </video>
                </figure>
            );
        default:
            return exhaustSwitchCase(projection.kind);
    }
};

// === Unavailable — the metadata-only floor (ReadingShape.unavailable) ========

const UnavailableReading = ({
    projection,
    ...deps
}: ReadingProps): ReactNode => {
    const { t, i18n } = useLingui();
    if (projection.type !== "unavailable") {
        return <FlatReading projection={projection} {...deps} />; // unreachable
    }
    // Localize the typed reason HERE, at the render boundary — the projection
    // carries a variant, never a sentence. Lead with what the author still has
    // (notes, summary, lineage all render), not with a "can't".
    const { reason } = projection;
    const onReupload = deps.onReupload;
    return reason.type === "content-missing" ? (
        <EmptyState
            title={t`This source's content is missing`}
            body={i18n._(unavailableReasonMessage(reason))}
        >
            {onReupload === undefined ? null : (
                <Button variant="primary" size="md" onClick={onReupload}>
                    <Icon name="upload" size="sm" />
                    {t`Upload again`}
                </Button>
            )}
        </EmptyState>
    ) : (
        <EmptyState
            title={t`This source can't be read here yet`}
            body={i18n._(unavailableReasonMessage(reason))}
        />
    );
};

// The reason → MessageDescriptor map (pure `msg` descriptors, localized by the
// caller — same discipline as `externalError.toDisplay`). Total over the closed
// reason sum; a new reason variant must add its clause before this compiles.
const unavailableReasonMessage = (
    reason: UnavailableReason,
): MessageDescriptor => {
    switch (reason.type) {
        case "text-not-stored":
            return msg`Its notes, summary, and anything made from it are all here — the source's full text just isn't available to read in this pane yet.`;
        case "content-missing":
            return msg`The stored file is gone, so neither you nor the assistant can read it. Upload the original file again to restore it here — everything that refers to this source keeps working.`;
        default:
            return exhaustSwitchCase(reason);
    }
};

// === The registry — keyed by reading shape, total by construction ===========

// Keyed by the CLOSED reading shape: renderer count grows with geometries, not
// formats. `outlined` and `sequential` are present-but-deferred — no codec
// emits their structure yet, so they route to `Flat` (§4 rule 1). When ingest
// grows `ExtractOutput` beyond `{ text }` and a projection variant maps to
// them, swap the entry for the real renderer — the total `Record` forces it.
// deferred: gated on ingest-enrichment (see source-view-per-kind.md §3.2/§3.3).
const RENDERERS: Record<ReadingShape, ReadingRenderer> = {
    paginated: PaginatedReading,
    outlined: FlatReading, // deferred: gated on ingest-enrichment
    sequential: FlatReading, // deferred: gated on ingest-enrichment
    synced: SyncedReading,
    image: ImageReading,
    media: MediaReading,
    flat: FlatReading,
    unavailable: UnavailableReading,
};

// The shell's protagonist slot: one lookup keyed by the projection's shape.
export const SourceReading = (props: ReadingProps): ReactNode => {
    const Renderer = RENDERERS[shapeOf(props.projection)];
    return <Renderer {...props} />;
};
